import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";
import { countInboundShipments } from "../../graphql/inbound/custom-queries";
import { countOutboundShipments } from "../../graphql/outbound/custom-queries";
import {
  getCustomerReloadYardAccountMini,
  getReloadYard,
} from "../../graphql/reloadyard/custom-queries";

import {
  Backdrop,
  Button,
  CardActions,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import IdeaIcon from "@material-ui/icons/WbIncandescentOutlined";

import { useNotify, Title } from "react-admin";

import {
  CloseIcon,
  EmailIcon,
  InbounddOutlinedIcon,
  OutbounddOutlinedIcon,
  YardSetupIcon,
} from "../../components/common/iconography";
import { ImageField } from "../../components/common/ImageField";
import CrewCard from "../../components/common/CrewCard";

import {
  logError,
  setupCommodityMaps,
  setupLocationMaps,
} from "../../utils/appUtils";
import { InboundStatus, OutboundStatus } from "../../utils/constants";
import { useUser } from "../../authenticatorApp/UserContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(1),
  },
  card: {
    minHeight: 150,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    flex: "1",
    "& a": {
      textDecoration: "none",
      color: "inherit",
    },
  },
  cardAvatar: {
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  cardItemLeft: {
    display: "flex",
    justifyContent: "flex-end",
  },
  cardItemRight: {
    display: "flex",
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  cardActions: {
    marginTop: "auto",
    "& a": {
      textDecoration: "none",
      color: "inherit",
    },
  },
  centerLogo: {
    display: "flex",
    justifyContent: "center", // Center horizontally
    alignItems: "center", // Center vertically
    // height: '100%',
  },
  cardWithBackdrop: {
    position: "relative",
    overflow: "hidden",
  },
  localBackdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: theme.zIndex.modal,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const sendFeatureRequestEmail = (appUser, message) => {
  const { firstName, lastName, email, accountName, reloadYard } = appUser;
  const htmlContent = `
    <p><strong>From:</strong> ${firstName} ${lastName} &lt;${email}&gt;</p>
    <p><strong>Account:</strong> ${accountName}</p>
    <p><strong>Yard:</strong> ${reloadYard?.name}</p>
    <hr/>
    <div style="white-space: pre-line;">${message}</div>
  `;
  return API.post("transREST", "/sendEmail", {
    body: {
      to: "support@transloads.co",
      subject: "Feature Request from Customer Portal",
      template: "emailTemplate.html",
      htmlContent,
    },
  });
};

export const CustomerDashboard = (props) => {
  const classes = useStyles();
  const { appUser, setAppUser, isMultiTenant } = useUser();

  return (
    <div className={classes.root}>
      <Title title=" " />
      <Grid container>
        <Grid item xs={12}>
          <FeatureRequestCard appUser={appUser} classes={classes} />
        </Grid>
        <Grid item xs={12}>
          {isMultiTenant() ? (
            <ReloadYardsCard
              appUser={appUser}
              setAppUser={setAppUser}
              classes={classes}
            />
          ) : (
            <ReloadYardCard appUser={appUser} classes={classes} />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <InboundCard appUser={appUser} classes={classes} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <OutboundCard appUser={appUser} classes={classes} />
        </Grid>
      </Grid>
    </div>
  );
};

const FeatureRequestCard = ({ appUser, classes, ...props }) => {
  const notify = useNotify();
  const cardRef = useRef(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [hidden, setHidden] = useState(false);
  const storageKey = `featureRequestHidden_${appUser?.id}`;

  useEffect(() => {
    const hiddenUntil = localStorage.getItem(storageKey);
    if (hiddenUntil && new Date(hiddenUntil) > new Date()) {
      setHidden(true);
    }
  }, [storageKey]);

  const hideFor30Days = () => {
    const hideUntil = new Date();
    hideUntil.setDate(hideUntil.getDate() + 30);
    // hideUntil.setSeconds(hideUntil.getSeconds() + 30); // hide for 30sec
    localStorage.setItem(storageKey, hideUntil.toISOString());
    setHidden(true);
  };

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await sendFeatureRequestEmail(appUser, message.trim());
      setMessage("");
      notify("Message received. Thank you!", "info");
      hideFor30Days();
    } catch (error) {
      logError("FeatureRequestCard:handleSubmit", error, notify);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    hideFor30Days();
  };

  if (hidden) return null;
  return (
    <div ref={cardRef} className={classes.cardWithBackdrop}>
      <CrewCard
        showDivider={false}
        title="Want more?"
        subheader="request a new feature..."
        icon={<IdeaIcon fontSize="large" />}
        addMargin={true}
        action={
          <IconButton edge="start" onClick={() => handleClose()}>
            <CloseIcon fontSize="large" />
          </IconButton>
        }
      >
        <Backdrop
          open={sending}
          className={classes.localBackdrop}
          disablePortal
          container={() => cardRef.current}
        >
          <CircularProgress />
        </Backdrop>

        <Grid container spacing={2}>
          <Grid item xs={12} md={11}>
            <TextField
              variant="outlined"
              multiline
              fullWidth
              minRows={2}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Let us know how we can improve transloads.co for you..."
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <div
              style={{
                height: "100%",
                display: "flex",
                alignItems: "self-end",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                fullWidth
                startIcon={<EmailIcon />}
                disabled={sending || !message.trim()}
                onClick={handleSubmit}
              >
                Send
              </Button>
            </div>
          </Grid>
        </Grid>
      </CrewCard>
    </div>
  );
};

const ReloadYardCard = ({ appUser, classes, ...props }) => {
  const [reloadYard] = useState(appUser?.reloadYard);

  if (!reloadYard) return <></>;
  return (
    <CrewCard
      showDivider={false}
      title={reloadYard.name}
      icon={<YardSetupIcon fontSize="large" />}
      addMargin={true}
    >
      <Grid container spacing={2}>
        <Grid item xs={6} className={classes.centerLogo}>
          <ImageField
            source="logoImage"
            record={reloadYard}
            thumbWidth={300}
            thumbHeight={160}
            scaleThumb={true}
          />
        </Grid>
        <Grid item xs={6}>
          <Typography variant="overline">Address:</Typography>
          <Typography variant="body2">{reloadYard.street}</Typography>
          <Typography variant="body2">{`${reloadYard.city}, ${reloadYard.state} ${reloadYard.zipCode}, ${reloadYard.country}`}</Typography>
          <Typography variant="overline">Email:</Typography>
          <Typography variant="body2">{reloadYard.email}</Typography>
          <Typography variant="overline">Phone:</Typography>
          <Typography variant="body2">{reloadYard.phone}</Typography>
          <Typography variant="overline">Fax:</Typography>
          <Typography variant="body2">{reloadYard.fax}</Typography>
        </Grid>
      </Grid>
    </CrewCard>
  );
};

const ReloadYardsCard = ({ appUser, setAppUser, classes, ...props }) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [reloadYards] = useState(
    appUser.reloadYardAccounts.items.map((item) => item.reloadYard)
  );
  const reloadYard = appUser?.reloadYard;

  const handleYardChange = async (event) => {
    debugger;
    try {
      setLoading(true);
      const reloadYardId = event.target.value;
      const reloadYardAccountId = appUser.reloadYardAccounts.items.find(
        (item) => item.reloadYardId === reloadYardId
      ).id;

      // Create two promises for customerAccount and reloadYard
      const accountPromise = API.graphql(
        graphqlOperation(getCustomerReloadYardAccountMini, {
          id: reloadYardAccountId,
        })
      );
      const reloadYardPromise = API.graphql(
        graphqlOperation(getReloadYard, { id: reloadYardId })
      );

      // Use Promise.all to wait for both promises to resolve
      const [accountResponse, reloadYardResponse] = await Promise.all([
        accountPromise,
        reloadYardPromise,
      ]);

      const reloadYardAccount =
        accountResponse.data.getCustomerReloadYardAccount;
      const reloadYard = reloadYardResponse.data.getReloadYard;
      setupLocationMaps(reloadYard);
      setupCommodityMaps(reloadYard);

      // Build new user object safely
      const updatedUser = {
        ...appUser,
        reloadYardId,
        reloadYard,
        accountId: reloadYardAccount.accountId,
        accountName: reloadYardAccount.accountName,
        account: reloadYardAccount.account,
        permissionList: reloadYardAccount.acl?.reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
      };

      debugger;
      setAppUser(updatedUser);
    } catch (err) {
      logError(
        "CustomerDashboard:ReloadYardCard::handleYardChange",
        err,
        notify
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <CrewCard
      showDivider={false}
      title="Transloads"
      icon={<YardSetupIcon fontSize="large" />}
      addMargin={true}
    >
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                id="site-select"
                label="Transload"
                variant="outlined"
                size="small"
                fullWidth
                required
                value={reloadYard.id}
                onChange={handleYardChange}
                helperText="Select a transload to manage"
              >
                {reloadYards.map((yard) => (
                  <MenuItem key={yard.id} value={yard.id}>
                    {yard.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} className={classes.centerLogo}>
              {loading ? (
                <CircularProgress color="inherit" />
              ) : (
                <ImageField
                  source="logoImage"
                  record={reloadYard}
                  thumbWidth={300}
                  thumbHeight={105}
                  scaleThumb={true}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="overline">Address:</Typography>
          <Typography variant="body2">{reloadYard.street}</Typography>
          <Typography variant="body2">{`${reloadYard.city}, ${reloadYard.state} ${reloadYard.zipCode}, ${reloadYard.country}`}</Typography>
          <Typography variant="overline">Email:</Typography>
          <Typography variant="body2">{reloadYard.email}</Typography>
          <Typography variant="overline">Phone:</Typography>
          <Typography variant="body2">{reloadYard.phone}</Typography>
          <Typography variant="overline">Fax:</Typography>
          <Typography variant="body2">{reloadYard.fax}</Typography>
        </Grid>
      </Grid>
    </CrewCard>
  );
};

const InboundCard = ({ appUser, classes }) => {
  const [summary, setSummary] = useState({
    rolling: 0,
    notified: 0,
    placed: 0,
  });
  const notify = useNotify();

  // Counts are scoped to the session account; re-run when the customer
  // switches transload (new reloadYardId + accountId).
  useEffect(() => {
    appUser?.accountId && init();
  }, [appUser?.reloadYardId, appUser?.accountId]);

  const init = async () => {
    try {
      const queryList = [];
      queryList[0] = API.graphql(
        graphqlOperation(countInboundShipments, {
          filter: {
            reloadYardId: { eq: appUser?.reloadYardId },
            accountId: { eq: appUser?.accountId },
            or: [
              { status: { eq: InboundStatus.IN_TRANSIT } },
              { status: { eq: InboundStatus.SIT_PENDING } },
            ],
          },
        })
      );

      queryList[1] = API.graphql(
        graphqlOperation(countInboundShipments, {
          filter: {
            reloadYardId: { eq: appUser?.reloadYardId },
            accountId: { eq: appUser?.accountId },
            status: { eq: InboundStatus.ARRIVED },
          },
        })
      );

      queryList[2] = API.graphql(
        graphqlOperation(countInboundShipments, {
          filter: {
            reloadYardId: { eq: appUser?.reloadYardId },
            accountId: { eq: appUser?.accountId },
            or: [
              { status: { eq: InboundStatus.PLACED } },
              { status: { eq: InboundStatus.SIT_PLACED } },
            ],
          },
        })
      );

      const promiseResults = await Promise.all(queryList);

      let rollingCount = promiseResults[0].data.searchInboundShipments.total;
      let notifiedCount = promiseResults[1].data.searchInboundShipments.total;
      let placedCount = promiseResults[2].data.searchInboundShipments.total;

      setSummary({
        rolling: rollingCount ? rollingCount : 0,
        notified: notifiedCount ? notifiedCount : 0,
        placed: placedCount ? placedCount : 0,
      });
    } catch (err) {
      logError("components:Dashboard:InboundCard:init", err, notify);
    }
  };

  return (
    <CrewCard
      showDivider={false}
      title="Inbound"
      icon={<InbounddOutlinedIcon fontSize="large" />}
      addMargin={true}
    >
      <CardContent>
        <div className={classes.card}>
          <Grid container spacing={1}>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.rolling}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                In Transit
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.notified}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Notified
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.placed}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Placed
              </Typography>
            </Grid>
          </Grid>
        </div>
      </CardContent>
      <CardActions className={classes.cardActions}>
        <Link to={"/inboundShipments"}>
          <Button size="large">View</Button>
        </Link>
      </CardActions>
    </CrewCard>
  );
};

const OutboundCard = ({ appUser, classes }) => {
  const [summary, setSummary] = useState({
    pending: 0,
    arrived: 0,
    placed: 0,
    loaded: 0,
  });
  const notify = useNotify();

  // Counts are scoped to the session account; re-run when the customer
  // switches transload (new reloadYardId + accountId).
  useEffect(() => {
    appUser?.accountId && init();
  }, [appUser?.reloadYardId, appUser?.accountId]);

  const init = async () => {
    try {
      const queryList = [];
      queryList[0] = API.graphql(
        graphqlOperation(countOutboundShipments, {
          filter: {
            reloadYardId: { eq: appUser?.reloadYardId },
            accountId: { eq: appUser?.accountId },
            status: { eq: OutboundStatus.PENDING },
          },
        })
      );

      queryList[1] = API.graphql(
        graphqlOperation(countOutboundShipments, {
          filter: {
            reloadYardId: { eq: appUser?.reloadYardId },
            accountId: { eq: appUser?.accountId },
            status: { eq: OutboundStatus.ARRIVED },
          },
        })
      );

      queryList[2] = API.graphql(
        graphqlOperation(countOutboundShipments, {
          filter: {
            reloadYardId: { eq: appUser?.reloadYardId },
            accountId: { eq: appUser?.accountId },
            status: { eq: OutboundStatus.PLACED },
          },
        })
      );

      queryList[3] = API.graphql(
        graphqlOperation(countOutboundShipments, {
          filter: {
            reloadYardId: { eq: appUser?.reloadYardId },
            accountId: { eq: appUser?.accountId },
            status: { eq: OutboundStatus.LOADED },
          },
        })
      );

      const promiseResults = await Promise.all(queryList);

      let pendingCount = promiseResults[0].data.searchOutboundShipments.total;
      let arrivedCount = promiseResults[1].data.searchOutboundShipments.total;
      let placedCount = promiseResults[2].data.searchOutboundShipments.total;
      let loadedCount = promiseResults[3].data.searchOutboundShipments.total;

      setSummary({
        pending: pendingCount ? pendingCount : 0,
        arrived: arrivedCount ? arrivedCount : 0,
        placed: placedCount ? placedCount : 0,
        loaded: loadedCount ? loadedCount : 0,
      });
    } catch (err) {
      logError("components:Dashboard:OutboundCard:init", err, notify);
    }
  };

  return (
    <CrewCard
      showDivider={false}
      title="Outbound"
      icon={<OutbounddOutlinedIcon fontSize="large" />}
      addMargin={true}
    >
      <CardContent>
        <div className={classes.card}>
          <Grid container spacing={1}>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.pending}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Pending
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.arrived}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Arrived
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.placed}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Placed
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography className={classes.cardItemLeft} variant="h4">
                {summary.loaded}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <Typography
                className={classes.cardItemRight}
                variant="h6"
                color="textSecondary"
              >
                Loaded
              </Typography>
            </Grid>
          </Grid>
        </div>
      </CardContent>
      <CardActions className={classes.cardActions}>
        <Link to={"/outboundShipments"}>
          <Button size="large">View</Button>
        </Link>
      </CardActions>
    </CrewCard>
  );
};
